import { ApprenticeshipRegistrationType } from "@/types/apprenticeship/apprentice";
import { social_minimum_types } from "@/utils/constants";

/**
 * Liste blanche Contrat pro (codes `sf_product.product_code`) :
 * - 12 mois : CAP AEPE, TP ADVF, SAMA
 * - 18 mois : CAP AEPE, TP ADVF, SAMA, TP NTC, TP ARH, TP GP
 */
export const MONTH_12_PRODUCT_CODES = new Set(["cap_aepe_alt", "tp_advf_alt", "tp_sama_alt"]);
export const MONTH_18_PRODUCT_CODES = new Set([
    "cap_aepe_alt",
    "tp_advf_alt",
    "tp_sama_alt",
    "tp_ntc_alt",
    "tp_arh_alt",
    "tp_gp_alt"
]);

/** Same upper bound as the situation form (months). */
export const POLE_EMPLOI_DURATION_MAX_MONTHS = 120;

const FT_18M_DURATION_EXCEEDED_MESSAGE =
    "Pour une formation de 18 mois, dans le cadre d'un contrat pro, l’inscription à France Travail doit dater d'au moins 12 mois.";

export function productAllows12Months(productCode: string | null | undefined): boolean {
    return productCode != null && MONTH_12_PRODUCT_CODES.has(productCode);
}

export function productAllows18Months(productCode: string | null | undefined): boolean {
    return productCode != null && MONTH_18_PRODUCT_CODES.has(productCode);
}

function socialMinimumSelected(r: ApprenticeshipRegistrationType): boolean {
    return social_minimum_types.map(smt => smt.label).includes(r.social_minimum_type || "");
}

/**
 * Months stored as digits only (e.g. "14"). Rejects free text like "7 septembre 2017"
 * (parseInt would wrongly return 7).
 */
export function parsePoleEmploiDurationMonths(raw: string | null | undefined): number | undefined {
    if (raw == null || String(raw).trim() === "") {
        return undefined;
    }
    const s = String(raw).trim();
    if (!/^\d{1,3}$/.test(s)) {
        return undefined;
    }
    const n = Number.parseInt(s, 10);
    return Number.isFinite(n) ? n : undefined;
}

/** Non-empty value that is not a plain digit month count (legacy row data). */
export function poleEmploiDurationLegacyText(raw: string | null | undefined): string | null {
    if (raw == null || String(raw).trim() === "") {
        return null;
    }
    return parsePoleEmploiDurationMonths(raw) === undefined ? String(raw).trim() : null;
}

/** Valid France Travail duration in months for Contrat Pro (aligné avec l’action `validate` côté API). */
export function poleEmploiDurationMonthsValidForContratPro(r: ApprenticeshipRegistrationType): boolean {
    if (!r.is_registered_to_pole_emploi) {
        return true;
    }
    const months = parsePoleEmploiDurationMonths(r.pole_emploi_duration ?? undefined);
    if (months === undefined || months < 1) {
        return false;
    }
    if (months > POLE_EMPLOI_DURATION_MAX_MONTHS) {
        return false;
    }
    const length = r.apprenticeship_session_length ?? 12;
    const productCode = r.sf_product?.product_code ?? null;
    if (length === 18 && !productAllows18Months(productCode)) {
        return false;
    }
    if (length === 18 && productAllows18Months(productCode)) {
        return months >= 12;
    }
    return true;
}

/** Whether a ContratPro registration passes rules checked on « Valider » (dossier). */
export function contratProMeetsSubmitRules(r: ApprenticeshipRegistrationType): boolean {
    const length = r.apprenticeship_session_length ?? 12;
    const productCode = r.sf_product?.product_code ?? null;

    if (length === 12 && !productAllows12Months(productCode)) {
        return false;
    }
    if (length === 18 && !productAllows18Months(productCode)) {
        return false;
    }
    if (length === 18) {
        if (socialMinimumSelected(r)) {
            return true;
        }
        if (r.is_registered_to_pole_emploi) {
            return poleEmploiDurationMonthsValidForContratPro(r);
        }
        return false;
    }

    if (r.is_registered_to_pole_emploi) {
        return poleEmploiDurationMonthsValidForContratPro(r);
    }
    return socialMinimumSelected(r);
}

/** Human-readable reasons when {@link contratProMeetsSubmitRules} is false (Contrat Pro only). */
export function getSubmitBlockingMessages(r: ApprenticeshipRegistrationType): string[] {
    const length = r.apprenticeship_session_length ?? 12;
    const productCode = r.sf_product?.product_code ?? null;

    if (length === 12 && !productAllows12Months(productCode)) {
        return [
            "La durée de 12 mois n'est disponible que pour les formations CAP AEPE, TP ADVF et SAMA."
        ];
    }
    if (length === 18 && !productAllows18Months(productCode)) {
        return [
            "La durée de 18 mois n'est disponible que pour les formations CAP AEPE, TP ADVF, SAMA, TP NTC, TP ARH et TP GP."
        ];
    }

    if (r.apprenticeship_type !== "ContratPro") {
        return [];
    }
    if (contratProMeetsSubmitRules(r)) {
        return [];
    }
    if (length === 18) {
        if (!socialMinimumSelected(r) && r.is_registered_to_pole_emploi) {
            const months = parsePoleEmploiDurationMonths(r.pole_emploi_duration ?? undefined);
            if (months === undefined || months < 1) {
                return [
                    "Indiquez la durée d'inscription à France Travail (en mois), au moins 1 mois."
                ];
            }
            if (months > POLE_EMPLOI_DURATION_MAX_MONTHS) {
                return [
                    `La durée d'inscription à France Travail ne peut pas dépasser ${POLE_EMPLOI_DURATION_MAX_MONTHS} mois.`
                ];
            }
            if (months < 12) {
                return [FT_18M_DURATION_EXCEEDED_MESSAGE];
            }
        }
        if (!socialMinimumSelected(r) && !r.is_registered_to_pole_emploi) {
            return [
                "Pour un contrat de professionnalisation à 18 mois, indiquez un minimum social (RSA, ASS ou AAH) ou une inscription France Travail depuis au moins 12 mois."
            ];
        }
        return ["Le dossier ne respecte pas les règles du contrat de professionnalisation."];
    }

    if (r.is_registered_to_pole_emploi) {
        const months = parsePoleEmploiDurationMonths(r.pole_emploi_duration ?? undefined);
        if (months === undefined || months < 1) {
            return [
                "Indiquez la durée d'inscription à France Travail (en mois), au moins 1 mois."
            ];
        }
        if (months > POLE_EMPLOI_DURATION_MAX_MONTHS) {
            return [
                `La durée d'inscription à France Travail ne peut pas dépasser ${POLE_EMPLOI_DURATION_MAX_MONTHS} mois.`
            ];
        }
    }

    if (!r.is_registered_to_pole_emploi && !socialMinimumSelected(r)) {
        return [
            "Pour un contrat de professionnalisation, indiquez une inscription France Travail ou un minimum social (RSA, ASS ou AAH)."
        ];
    }

    return ["Le dossier ne respecte pas les règles du contrat de professionnalisation."];
}
